import { useMutation } from '@tanstack/react-query';
import axiosClient from 'api/config/axios';

interface InitiatePaymentResponse {
  success: boolean;
  payment: {
    amount: string;
    checkout_url: string;
    code: string;
    currency: string;
    driver: string;
    id: number;
    payment_method: string | null;
    status: string;
  };
  checkout_url: string;
}

/**
 * Initiates a payment for the given payment order, optionally specifying a payment provider
 * driver and a Xendit sub-method code (e.g. "GCASH"). The `payment_method` field is required
 * by the backend when the chosen driver has any configured methods (i.e. Xendit). PayPal
 * accepts requests without a `payment_method`.
 */
async function initiatePayment({
  driver,
  paymentMethod,
  paymentOrderId,
}: {
  paymentOrderId: number;
  driver?: string;
  paymentMethod?: string;
}): Promise<InitiatePaymentResponse> {
  const body: { driver?: string; payment_method?: string } = {};
  if (driver !== undefined) body.driver = driver;
  if (paymentMethod !== undefined) body.payment_method = paymentMethod;

  const result = await axiosClient.mutate.post<InitiatePaymentResponse>(
    `/api/v1/escrow/payment-orders/${paymentOrderId}/pay`,
    body,
  );
  return result.data;
}

const mutationKey = ['escrow', 'initiate-payment'] as const;

export function useInitiatePayment() {
  return useMutation({
    mutationKey,
    mutationFn: initiatePayment,
  });
}
